
import { computed, defineComponent, onMounted, PropType, reactive } from 'vue'

// masks
import { TIME_MASK } from '@/core/constants/masks.constants'

// components
import Modal from '@/common/components/UI/Modal/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import RadioGroup from '@/common/components/UI/RadioGroup/index.vue'
import FileReader from '@/common/components/UI/FileReader/index.vue'

// validations
import useVuelidate from '@vuelidate/core'
import { helpers, integer, required, requiredIf, decimal, minLength } from '@vuelidate/validators'
import {
    AdditionalServiceModalFTLI,
    AdditionalServicesFTLI,
    StocksI,
} from '@/core/types/additional_services.types'
import { carriers } from '@/core/realisations/carriers.realisation'
import { DataState } from '@/core/classes/DataState.class'
import { CarrierT } from '@/core/types/carriers.types'

// constants
import { FTL_ORDER_PAYMENT_RATE_TYPES } from '@/core/constants/ftl_types.constants'
import { TripComment } from '@/new-services/types/tms'
import { FTL } from '@/core/realisations/FTL.realisation'

export default defineComponent({
    components: {
        Modal,
        TheInput,
        TheSelect,
        Checkbox,
        RadioGroup,
        FileReader,
    },
    emits: ['cancel', 'submitted'],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        additionalServices: {
            type: Array as PropType<AdditionalServicesFTLI[]>,
            default: () => [],
        },
        stocks: {
            type: Array as PropType<StocksI[]>,
            default: () => [],
        },
        ftlType: {
            type: Number,
            default: null,
        },
        isNeedCar: {
            type: Boolean,
            default: false,
        },
        senderCity: {
            type: String,
            default: null,
        },
        statusCode: {
            type: String,
            default: null,
        },
        isComment: {
            type: Boolean,
            default: false,
        },
        stateId: {
            type: Number,
            required: true,
        },
    },
    setup(props, { emit }) {
        const state = reactive({
            is_loading: false,
        })

        const delivery_state = reactive({
            delivery_date: null as string | null,
            delivery_time: null as string | null,
            received_by: null as string | null,
            //
            auto_count: null as number | null,
            auto_duration: null as number | null,
            auto_duration_client: null as number | null,
            //
            additional_services: [] as AdditionalServiceModalFTLI[],
            additional_services_stock: [] as AdditionalServiceModalFTLI[][],
            //
            has_downtime: false,
            downtime: {
                type: 1,
                hours: null as string | number | null,
                cost_price: null as string | number | null,
            },
            //
            removed_stamp: [] as File[],
            carcass: [] as File[],
            //
            ttn: [] as File[],
            cmr: [] as File[],
            // delayCommentId: null as number | null,
        })

        const data_state = reactive(new DataState<CarrierT>({}))

        const data_state_comments = reactive({
            comments: {
                is_loading: false,
                is_failed: false,
                list: [] as TripComment[],
            },
        })

        const isFTLTypeOne = computed(() => props.ftlType === 1)
        const isFTLTypeTwo = () => props.ftlType === 2

        const isOnTheWay = computed(() => props.statusCode === 'on_the_way')
        const isCrossed = computed(
            () => props.statusCode === 'crossed_the_border' && props.ftlType === 1
        )

        const state_form$ = useVuelidate(
            {
                delivery_date: { required },
                delivery_time: { required, minLength: minLength(5) },
                received_by: { required },
                auto_count: {
                    required: requiredIf(() => isFTLTypeTwo() && props.isNeedCar),
                    integer,
                },
                auto_duration: {
                    required: requiredIf(() => isFTLTypeTwo() && props.isNeedCar),
                    decimal,
                },
                additional_services: {
                    // @ts-ignore
                    $each: helpers.forEach({
                        carrier_id: {
                            required: requiredIf(() => !isOnTheWay.value),
                            integer,
                        },
                        value: { required: requiredIf(() => !isOnTheWay.value), integer },
                        duration: { required: requiredIf(() => !isOnTheWay.value), integer },
                        cost_per_hour: { required: requiredIf(() => !isOnTheWay.value), integer },
                    }),
                },
                downtime: {
                    hours: {
                        required: requiredIf(
                            () =>
                                delivery_state.has_downtime && (isOnTheWay.value || isCrossed.value)
                        ),
                    },
                    cost_price: {
                        required: requiredIf(
                            () =>
                                delivery_state.has_downtime && (isOnTheWay.value || isCrossed.value)
                        ),
                    },
                },
                removed_stamp: {
                    required: requiredIf(() => isFTLTypeOne.value),
                },
                carcass: {
                    required: requiredIf(() => isFTLTypeOne.value),
                },
                auto_duration_client: {
                    required: requiredIf(() => isFTLTypeTwo() && props.isNeedCar),
                    integer,
                },
                ttn: { required: requiredIf(() => props.ftlType === 0) },
                cmr: { required: requiredIf(() => props.ftlType === 1) },
                // delayCommentId: { required: requiredIf(() => props.isComment) },
            },
            delivery_state
        )

        const loadCarriers = async (page: number) => {
            data_state.pagination.current_page = page

            try {
                const params = { page, getAllCarriers: 1, limit: 9999, statusId: 2 }

                data_state.is_loading = true
                const data = await carriers.getList(params)

                data_state.list = data.data
                data_state.pagination.total_pages = data.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.pagination.total_pages) return
            data_state.pagination.current_page = page

            try {
                const params = { page, getAllCarriers: 1, limit: 9999, statusId: 2 }

                data_state.is_loading = true

                const response = await carriers.getList(params)
                data_state.list = data_state.list.concat(response.data)

                data_state.pagination.total_pages = response.meta.last_page

                data_state.is_failed = false
                data_state.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.failure.catchError(error)

                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_failed = true
                }
            } finally {
                if (!data_state.failure.error_state.is_cancel) {
                    data_state.is_loading = false
                }
            }
        }

        const emitCancel = () => {
            emit('cancel')
        }

        const emitSubmit = () => {
            const payload = {
                ...delivery_state,
                payload: [...delivery_state.additional_services],
            }

            if (delivery_state.additional_services_stock.length) {
                delivery_state.additional_services_stock.forEach((block) => {
                    block.forEach((service) => {
                        payload.payload.push(service)
                        payload.additional_services.push(service)
                    })
                })
            }

            emit('submitted', payload)
        }

        const emitSubmitOnTheWay = () => {
            const payload = {
                delivery_date: delivery_state.delivery_date,
                delivery_time: delivery_state.delivery_time,
                received_by: delivery_state.received_by,
                auto_count: delivery_state.auto_count,
                auto_duration: delivery_state.auto_duration,
                auto_duration_client: delivery_state.auto_duration_client,
                has_downtime: Number(delivery_state.has_downtime),
                downtime: {
                    type: delivery_state.downtime.type,
                    hours: Number(delivery_state.downtime.hours),
                    cost_price: Number(delivery_state.downtime.cost_price),
                },
                carcass: delivery_state.carcass,
                removed_stamp: delivery_state.removed_stamp,
                // delayCommentId: delivery_state.delayCommentId,
                ttn: delivery_state.ttn,
                cmr: delivery_state.cmr,
            }

            emit('submitted', payload)
        }

        const manageSubmitted = () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (isOnTheWay.value || isCrossed.value) emitSubmitOnTheWay()
            else emitSubmit()
        }

        const isHaveDuration = (type_id: number) => {
            return type_id !== 1 && type_id !== 3 && type_id !== 4 && type_id !== 5 && type_id !== 6
        }

        const isHaveValue = (type_id: number) => {
            return type_id !== 1
        }

        const fillAdditionalServices = () => {
            props.additionalServices?.forEach((service) => {
                let new_service: AdditionalServiceModalFTLI = {
                    id: service.id,
                    type_code: service.type_code,
                    type_id: service.type_id,
                    type_name: service.type_name,
                    carrier_id: service.carrier_id,
                    cost_per_hour: service.cost_per_hour,
                    value: Number(service.value),
                }

                if (service.type_id !== 1) new_service.value = Number(service.value)
                if (isHaveDuration(service.type_id)) new_service.duration = null

                delivery_state.additional_services.push({ ...new_service })
            })
        }

        const fillAdditionalServicesStock = () => {
            if (!props.stocks.length) return

            props.stocks.forEach((stock) => {
                if (stock.additionalServices && stock.additionalServices.length) {
                    const services: AdditionalServiceModalFTLI[] = []

                    stock.additionalServices.forEach((service) => {
                        let new_service: AdditionalServiceModalFTLI = {
                            id: service.id,
                            type_code: service.type_code,
                            type_id: service.type_id,
                            type_name: service.type_name,
                            carrier_id: service.carrier_id,
                            cost_per_hour: service.cost_per_hour,
                            value: Number(service.value),
                        }

                        if (service.type_id !== 1) new_service.value = Number(service.value)
                        if (isHaveDuration(service.type_id)) new_service.duration = null

                        services.push({ ...new_service })
                    })

                    delivery_state.additional_services_stock.push(services)
                }
            })
        }

        const getPlaceholder = (placeholder: string) => {
            if (delivery_state.downtime) {
                return delivery_state.downtime.type === 2 ? `${placeholder} (за час)` : placeholder
            }

            return placeholder
        }

        const isHaveAdditionalServices = computed(() => Boolean(props.additionalServices.length))
        const isHaveAdditionalServicesStock = computed(() =>
            Boolean(props.stocks.length && delivery_state.additional_services_stock.length)
        )

        const isModalLoading = computed(() => {
            return props.isLoading || data_state.is_loading
        })

        const loadComments = async () => {
            try {
                data_state_comments.comments.is_loading = true

                const response = await FTL.getComments(props.stateId)
                data_state_comments.comments.list = response.data

                data_state_comments.comments.is_failed = false
            } catch (error) {
                data_state_comments.comments.is_failed = true
                console.error(error)
            } finally {
                data_state_comments.comments.is_loading = false
            }
        }

        onMounted(async () => {
            if (isOnTheWay.value) {
                loadComments()
                await loadCarriers(1)
                fillAdditionalServices()
                fillAdditionalServicesStock()

                return
            }
        })

        return {
            state,
            data_state,
            data_state_comments,
            delivery_state,
            state_form$,
            emitCancel,
            manageSubmitted,
            loadCarriers,
            loadMoreCarriers,
            isHaveDuration,
            isHaveValue,
            isFTLTypeTwo,
            isFTLTypeOne,
            getPlaceholder,
            //
            isHaveAdditionalServices,
            isHaveAdditionalServicesStock,
            isModalLoading,
            isOnTheWay,
            isCrossed,
            //
            TIME_MASK,
            FTL_ORDER_PAYMENT_RATE_TYPES,
            loadComments,
        }
    },
})
